import { Input } from "./Input";
import Utility from "../utils/utility";

/**
 * The three ways a minimum-payout top-up can be funded when a host's percentage
 * share falls below the configured minimum. Must mirror FUNDING_MODES in the
 * backend (gist-firebase-functions/src/users/payoutMath.js).
 */
export const FUNDING_MODE_OPTIONS = [
  {
    value: "platform_cut",
    label: "From the platform's cut (capped at collections)",
    desc: "Top up to the minimum, but never above what the event actually collected. The platform forgoes its fee — it never pays out of pocket. Safest default.",
  },
  {
    value: "out_of_pocket",
    label: "Platform pays out of pocket",
    desc: "The host always gets the full minimum, even if the event collected less. The platform funds the gap from its own balance.",
  },
];

const DEFAULT_FORM = {
  hostRatePct: 70,
  introEnabled: false,
  introRatePct: 80,
  introFirstNEvents: 2,
  introWindowType: "first", // "first" | "next"
  introAnchorMode: "count", // "count" | "date" (only meaningful when type is "next")
  introAnchorDate: "", // yyyy-mm-dd; blank = "starts today" (date anchor)
  introAnchorDateMs: null, // server-stamped instant of an existing count anchor (display only)
  introReanchor: false, // re-stamp the count anchor to now on save (count anchor, editing only)
  minEnabled: false,
  minAmountDollars: 60,
  firstNEvents: 2,
  minWindowType: "first",
  minAnchorMode: "count",
  minAnchorDate: "",
  minAnchorDateMs: null,
  minReanchor: false,
  fundingMode: "platform_cut",
  autoEnabled: false,
  autoPaused: false,
};

// ---- date display helpers ----
// The window's anchorDateMs is derived SERVER-SIDE (in the platform timezone)
// so it lines up with stored event dates; the client only sends/echoes the
// "yyyy-mm-dd" date string and never converts dates to epoch ms itself.

/** epoch ms -> "yyyy-mm-dd" (local), or "" when absent. Display fallback only. */
function msToDateStr(ms) {
  const d = Utility.convertDate(ms);
  if (!d) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

const formatDateMs = (ms) => {
  const d = Utility.convertDate(ms);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};

/** Format a "yyyy-mm-dd" string for display (parsed as a local calendar day). */
const formatDateStr = (s) => {
  const d = Utility.convertDate(s);
  return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
};

// ---- form <-> wire mapping for a section's event window ----

/** Read a stored/effective section's window into the flat per-section form fields. */
function windowToForm(section, defaultCount) {
  const w = section && section.window;
  if (w && w.type === "next") {
    const anchorMode = w.anchorMode === "count" ? "count" : "date";
    return {
      windowType: "next",
      count: Number.isInteger(w.count) ? w.count : defaultCount,
      anchorMode,
      // Date anchor: prefer the stored date string (no TZ round-trip); fall back
      // to deriving it from the instant for any legacy/partial data.
      anchorDate: anchorMode === "date" ? (w.anchorDate || msToDateStr(w.anchorDateMs)) : "",
      // Count anchor: the server-stamped instant, for the "anchored on …" display.
      anchorDateMs: anchorMode === "count" && Number.isInteger(w.anchorDateMs) ? w.anchorDateMs : null,
    };
  }
  // first-N: an explicit {type:"first"} window or the legacy firstNEvents scalar.
  const count = w && w.type === "first" && Number.isInteger(w.count)
    ? w.count
    : section && section.firstNEvents != null ? section.firstNEvents : defaultCount;
  return { windowType: "first", count, anchorMode: "count", anchorDate: "", anchorDateMs: null };
}

/**
 * Build the wire `window` object for a "next X" section. The anchor instant is
 * resolved on the server: a count anchor carries no date (stamped to "now"), a
 * date anchor carries the "yyyy-mm-dd" string (null = "starts today").
 */
function buildNextWindow(anchorMode, anchorDate, count, allowCountAnchor) {
  const mode = allowCountAnchor && anchorMode === "count" ? "count" : "date";
  if (mode === "count") {
    return { type: "next", count, anchorMode: "count" };
  }
  return { type: "next", count, anchorMode: "date", anchorDate: anchorDate || null };
}

/** Convert a stored/effective { hostRate, introRate, minPayout } config into form state. */
export function configToForm(cfg) {
  if (!cfg) return { ...DEFAULT_FORM };
  const intro = cfg.introRate || {};
  const min = cfg.minPayout || {};
  const iw = windowToForm(intro, DEFAULT_FORM.introFirstNEvents);
  const mw = windowToForm(min, DEFAULT_FORM.firstNEvents);
  return {
    hostRatePct: cfg.hostRate != null ? Math.round(cfg.hostRate * 1000) / 10 : DEFAULT_FORM.hostRatePct,
    introEnabled: !!intro.enabled,
    introRatePct: intro.rate != null ? Math.round(intro.rate * 1000) / 10 : DEFAULT_FORM.introRatePct,
    introFirstNEvents: iw.count,
    introWindowType: iw.windowType,
    introAnchorMode: iw.anchorMode,
    introAnchorDate: iw.anchorDate,
    introAnchorDateMs: iw.anchorDateMs,
    introReanchor: false,
    minEnabled: !!min.enabled,
    minAmountDollars: min.amountCents != null ? min.amountCents / 100 : DEFAULT_FORM.minAmountDollars,
    firstNEvents: mw.count,
    minWindowType: mw.windowType,
    minAnchorMode: mw.anchorMode,
    minAnchorDate: mw.anchorDate,
    minAnchorDateMs: mw.anchorDateMs,
    minReanchor: false,
    fundingMode: min.fundingMode || DEFAULT_FORM.fundingMode,
    autoEnabled: !!(cfg.autoPayout && cfg.autoPayout.enabled),
    autoPaused: !!(cfg.autoPayout && cfg.autoPayout.paused),
  };
}

/** Build the introRate wire object (or {enabled:false}) from form state. */
export function formToIntroRate(form, { allowCountAnchor = true } = {}) {
  if (!form.introEnabled) return { enabled: false };
  const rate = Number(form.introRatePct) / 100;
  const count = Math.round(Number(form.introFirstNEvents));
  if (form.introWindowType === "next") {
    const out = { enabled: true, rate, window: buildNextWindow(form.introAnchorMode, form.introAnchorDate, count, allowCountAnchor) };
    if (form.introReanchor) out.reanchor = true;
    return out;
  }
  return { enabled: true, rate, firstNEvents: count };
}

/** Build the minPayout wire object (or {enabled:false}) from form state. */
export function formToMinPayout(form, { allowCountAnchor = true } = {}) {
  if (!form.minEnabled) return { enabled: false };
  const amountCents = Math.round(Number(form.minAmountDollars) * 100);
  const count = Math.round(Number(form.firstNEvents));
  if (form.minWindowType === "next") {
    const out = {
      enabled: true,
      amountCents,
      fundingMode: form.fundingMode,
      window: buildNextWindow(form.minAnchorMode, form.minAnchorDate, count, allowCountAnchor),
    };
    if (form.minReanchor) out.reanchor = true;
    return out;
  }
  return { enabled: true, amountCents, firstNEvents: count, fundingMode: form.fundingMode };
}

/** Build the autoPayout wire object from form state. */
export function formToAutoPayout(form) {
  return { enabled: !!form.autoEnabled, paused: !!form.autoPaused };
}

/** Build the full { hostRate, introRate, minPayout, autoPayout } wire payload from form state. */
export function formToWire(form, opts) {
  return {
    hostRate: Number(form.hostRatePct) / 100,
    introRate: formToIntroRate(form, opts),
    minPayout: formToMinPayout(form, opts),
    autoPayout: formToAutoPayout(form),
  };
}

/**
 * Plain-English phrase for a saved/effective section's window — e.g. "first 2
 * events", "next 3 events", "next 3 events from Jul 1". Reused by the deal/config
 * summaries in PayoutOverrideForm and PayoutSettingsModal.
 */
export function describeSectionWindow(section) {
  const w = section && section.window;
  if (w && w.type === "next") {
    if (w.anchorMode === "date") {
      const day = w.anchorDate ? formatDateStr(w.anchorDate) : formatDateMs(w.anchorDateMs);
      return day ? `next ${w.count} event(s) from ${day}` : `next ${w.count} event(s)`;
    }
    return `next ${w.count} event(s)`;
  }
  const n = w && w.type === "first" && w.count != null ? w.count : section && section.firstNEvents;
  return `first ${n} event(s)`;
}

/** Plain-English phrase for a section window held in form state (pre-save). */
function windowPhraseFromForm(type, count, anchorMode, anchorDate) {
  if (type !== "next") return `first ${count} event(s)`;
  if (anchorMode === "date") return `next ${count} event(s)${anchorDate ? ` from ${formatDateStr(anchorDate)}` : " (starting today)"}`;
  return `next ${count} event(s)`;
}

/** A one-line plain-English summary of a form's split + boost + guarantee. */
export function summarizeForm(form, { allowCountAnchor = true } = {}) {
  // When the count anchor isn't available (global config), a "next X" window is
  // saved date-anchored regardless of the form's raw anchorMode — phrase it that way.
  const eff = (mode) => (allowCountAnchor ? mode : "date");
  const platformPct = Math.round((100 - Number(form.hostRatePct)) * 10) / 10;
  let s = `Host keeps ${form.hostRatePct}%, platform keeps ${platformPct}%.`;
  if (form.introEnabled) {
    const win = windowPhraseFromForm(form.introWindowType, form.introFirstNEvents, eff(form.introAnchorMode), form.introAnchorDate);
    s += ` ${form.introRatePct}% on the host's ${win}, then ${form.hostRatePct}%.`;
  }
  if (form.minEnabled) {
    const mode = FUNDING_MODE_OPTIONS.find((m) => m.value === form.fundingMode);
    const win = windowPhraseFromForm(form.minWindowType, form.firstNEvents, eff(form.minAnchorMode), form.minAnchorDate);
    s += ` Minimum $${form.minAmountDollars} on the host's ${win} (${mode ? mode.label.toLowerCase() : form.fundingMode}).`;
  }
  if (form.autoPaused) {
    s += " AUTOMATIC PAYOUTS ARE PAUSED — no host will be paid automatically, including per-host opt-ins.";
  } else {
    s += form.autoEnabled
      ? " Matured funds are paid out automatically every day."
      : " Hosts request payouts themselves.";
  }
  return s;
}

// Maps a section ("intro" | "min") to its flat form-field keys so one control
// renderer drives both the boost and the minimum-guarantee windows.
const WINDOW_KEYS = {
  intro: { type: "introWindowType", count: "introFirstNEvents", anchorMode: "introAnchorMode", anchorDate: "introAnchorDate", anchorDateMs: "introAnchorDateMs", reanchor: "introReanchor" },
  min: { type: "minWindowType", count: "firstNEvents", anchorMode: "minAnchorMode", anchorDate: "minAnchorDate", anchorDateMs: "minAnchorDateMs", reanchor: "minReanchor" },
};

/**
 * The window picker shown inside both the boost and minimum-guarantee sections:
 * choose "First N events" or "Next X events", and for "next" choose the anchor.
 * `allowCountAnchor` is true per-host (offers the count snapshot) and false for
 * the global default (date anchor only — there's no single host to count).
 */
function EventWindowControl({ kind, value, set, idPrefix, allowCountAnchor }) {
  const k = WINDOW_KEYS[kind];
  const type = value[k.type];
  const anchorMode = allowCountAnchor ? value[k.anchorMode] : "date";
  const count = value[k.count] || "X";
  const noun = kind === "intro" ? "boosted rate" : "minimum";

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label={type === "next" ? "Applies to next X events" : "Applies to first N events"}
          id={`${idPrefix}-${kind}-count`}
          type="number"
          min="1"
          step="1"
          value={value[k.count]}
          onChange={(e) => set({ [k.count]: e.target.value })}
          required={false}
        />
        <div>
          <span className="block text-sm font-medium leading-6 text-gray-900 dark:text-gray-100">Window</span>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {[{ v: "first", label: "First N" }, { v: "next", label: "Next X" }].map((opt) => (
              <button
                key={opt.v}
                type="button"
                onClick={() => set({ [k.type]: opt.v })}
                className={`rounded-md border px-3 py-1.5 text-sm font-medium ${
                  type === opt.v
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {type === "next" && (
        <div className="rounded-md bg-gray-50 dark:bg-gray-700/40 p-3 space-y-3">
          {allowCountAnchor && (
            <div className="space-y-1.5">
              <span className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Start the window…</span>
              {[
                { v: "count", label: "From the host's next event", hint: "Starts now — the deal applies to the host's events going forward (their already-completed events are excluded)." },
                { v: "date", label: "From a start date", hint: "The deal applies to the host's events on/after the date below." },
              ].map((opt) => (
                <label key={opt.v} className="flex cursor-pointer items-start gap-x-2.5 rounded-md p-1.5 hover:bg-white dark:hover:bg-gray-700">
                  <input
                    type="radio"
                    name={`${idPrefix}-${kind}-anchor`}
                    checked={anchorMode === opt.v}
                    onChange={() => set({ [k.anchorMode]: opt.v })}
                    className="mt-1 h-4 w-4 border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  <span>
                    <span className="block text-sm text-gray-800 dark:text-gray-200">{opt.label}</span>
                    <span className="block text-xs text-gray-400 dark:text-gray-500">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {anchorMode === "date" && (
            <div className="sm:w-2/3">
              <Input
                label="Start date"
                id={`${idPrefix}-${kind}-anchorDate`}
                type="date"
                value={value[k.anchorDate]}
                onChange={(e) => set({ [k.anchorDate]: e.target.value })}
                required={false}
              />
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">Leave blank to start today.</p>
            </div>
          )}

          {allowCountAnchor && anchorMode === "count" && (
            value[k.anchorDateMs] != null ? (
              <div className="text-xs text-gray-500 dark:text-gray-400">
                <p>
                  Anchored on <strong>{formatDateMs(value[k.anchorDateMs])}</strong> — the {noun} applies to the host's next {count} event(s) from then.
                </p>
                <label className="mt-1.5 flex items-center gap-x-2">
                  <input
                    type="checkbox"
                    checked={!!value[k.reanchor]}
                    onChange={(e) => set({ [k.reanchor]: e.target.checked })}
                    className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                  />
                  <span>Re-anchor to now (restart from the host's next event)</span>
                </label>
              </div>
            ) : (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Applies to the host's next {count} event(s); the start is anchored to the moment you save.
              </p>
            )
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The shared rate + minimum-guarantee form fields. Stateless — the parent owns
 * `value` (a form object) and `onChange(nextForm)`. Used by the global payout
 * settings page and the per-host override section. `allowCountAnchor` enables the
 * per-host "from their next event" (count snapshot) anchor; the global default
 * passes false so "next X" windows there are date-anchored only.
 */
export default function PayoutFields({ value, onChange, idPrefix = "payout", showRate = true, showMin = true, showAuto = false, allowCountAnchor = false }) {
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-5">
      {showRate && (
      <div className="space-y-5">
        <div className="sm:w-1/2">
          <Input
            label="Host share (%)"
            id={`${idPrefix}-hostRate`}
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={value.hostRatePct}
            onChange={(e) => set({ hostRatePct: e.target.value })}
            required={false}
          />
          <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
            The platform keeps the remaining{" "}
            {Number.isFinite(Number(value.hostRatePct))
              ? Math.round((100 - Number(value.hostRatePct)) * 10) / 10
              : "—"}
            %.
          </p>
        </div>

        <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
          <label className="flex items-center gap-x-3">
            <input
              type="checkbox"
              checked={value.introEnabled}
              onChange={(e) => set({ introEnabled: e.target.checked })}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
              Boost the host's share for a window of events
            </span>
          </label>

          {value.introEnabled && (
            <div className="mt-4 space-y-3">
              <div className="sm:w-1/2">
                <Input
                  label="Boosted host share (%)"
                  id={`${idPrefix}-introRate`}
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={value.introRatePct}
                  onChange={(e) => set({ introRatePct: e.target.value })}
                  required={false}
                />
              </div>
              <EventWindowControl kind="intro" value={value} set={set} idPrefix={idPrefix} allowCountAnchor={allowCountAnchor} />
              <p className="text-xs text-gray-400 dark:text-gray-500">
                For the host's {windowPhraseFromForm(value.introWindowType, value.introFirstNEvents || "N", allowCountAnchor ? value.introAnchorMode : "date", value.introAnchorDate)} they keep{" "}
                {value.introRatePct}%, otherwise {value.hostRatePct}%.
              </p>
            </div>
          )}
        </div>
      </div>
      )}

      {showMin && (
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
        <label className="flex items-center gap-x-3">
          <input
            type="checkbox"
            checked={value.minEnabled}
            onChange={(e) => set({ minEnabled: e.target.checked })}
            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
            Guarantee a minimum payout for a window of events
          </span>
        </label>

        {value.minEnabled && (
          <div className="mt-4 space-y-4">
            <div className="sm:w-1/2">
              <Input
                label="Minimum payout ($)"
                id={`${idPrefix}-minAmount`}
                type="number"
                min="1"
                step="1"
                value={value.minAmountDollars}
                onChange={(e) => set({ minAmountDollars: e.target.value })}
                required={false}
              />
            </div>

            <EventWindowControl kind="min" value={value} set={set} idPrefix={idPrefix} allowCountAnchor={allowCountAnchor} />

            <fieldset>
              <legend className="text-sm font-medium text-gray-500 dark:text-gray-400">
                When the percentage share is below the minimum…
              </legend>
              <div className="mt-2 space-y-2">
                {FUNDING_MODE_OPTIONS.map((m) => (
                  <label
                    key={m.value}
                    className="flex cursor-pointer items-start gap-x-3 rounded-md border border-gray-200 dark:border-gray-700 p-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <input
                      type="radio"
                      name={`${idPrefix}-fundingMode`}
                      value={m.value}
                      checked={value.fundingMode === m.value}
                      onChange={() => set({ fundingMode: m.value })}
                      className="mt-1 h-4 w-4 border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
                    />
                    <span>
                      <span className="block text-sm font-medium text-gray-800 dark:text-gray-200">{m.label}</span>
                      <span className="block text-xs text-gray-400 dark:text-gray-500">{m.desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>
        )}
      </div>
      )}

      {showAuto && (
      <div className="rounded-md border border-gray-200 dark:border-gray-700 p-4">
        <label className="flex items-start gap-x-3">
          <input
            type="checkbox"
            checked={!!value.autoEnabled}
            onChange={(e) => set({ autoEnabled: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary"
          />
          <span>
            <span className="block text-sm font-medium text-gray-700 dark:text-gray-200">
              Pay hosts automatically when funds mature
            </span>
            <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">
              A daily run sends each host's available balance to their bank via Stripe, 3 days after
              each event. Hosts are notified by text and push. Hosts without Stripe onboarding are
              skipped until they finish it; the manual Request Payout button keeps working either way.
            </span>
          </span>
        </label>

        <label className="mt-4 flex items-start gap-x-3 border-t border-gray-100 dark:border-gray-700 pt-4">
          <input
            type="checkbox"
            checked={!!value.autoPaused}
            onChange={(e) => set({ autoPaused: e.target.checked })}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500"
          />
          <span>
            <span className="block text-sm font-medium text-red-700 dark:text-red-400">
              Emergency stop: pause all automatic payouts
            </span>
            <span className="mt-0.5 block text-xs text-gray-400 dark:text-gray-500">
              Halts the daily run for every host, including those individually set to always
              auto-pay. Use this to stop payouts right now; unchecking the box above only changes
              the default for hosts that follow it.
            </span>
          </span>
        </label>
      </div>
      )}
    </div>
  );
}
